<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Add Violation Detail</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-10 col-lg-10 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <template v-if="hasCreatePermission">
                            <router-link to="/violation/parameters/view">
                                <button type="button" class="btn btn-danger mb-2 me-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-arrow-left">
                                        <line x1="19" y1="12" x2="5" y2="12"></line>
                                        <polyline points="12 19 5 12 12 5"></polyline>
                                    </svg>
                                    Back to Violations Details List
                                </button>
                            </router-link>
                            <h6>Add Violation Details...</h6>

                            <!-- Form -->
                            <form @submit.prevent="submitForm">
                                <div class="column">
                                    <div class="col-md-12">
                                        <div class="mb-4">
                                            <label class="col-form-label" for="violation_id">Select Violation</label>
                                            <select v-model="violationParameter.violation_id" id="violation_id"
                                                class="form-control" required :disabled="loading">
                                                <option value="" disabled>Select a violation</option>
                                                <option v-for="violation in violations" :key="violation.id"
                                                    :value="violation.id">
                                                    {{ violation.title }}
                                                </option>
                                            </select>
                                        </div>

                                        <div class="mb-4">
                                            <label class="col-form-label" for="name">Detail Name</label>
                                            <input v-model="violationParameter.name" type="text" id="name"
                                                class="form-control" placeholder="e.g., Max Speed, Location, Reason"
                                                required :disabled="loading" />
                                        </div>

                                        <div class="mb-4">
                                            <label class="col-form-label" for="parameter_type_id">Select Detail
                                                Type</label>
                                            <select v-model="violationParameter.parameter_type_id"
                                                id="parameter_type_id" class="form-control" required
                                                :disabled="loading">
                                                <option value="" disabled>Select a Detail Type</option>
                                                <option v-for="parameter_type in parameter_types"
                                                    :key="parameter_type.id" :value="parameter_type.id">
                                                    {{ parameter_type.type }}
                                                </option>
                                            </select>
                                        </div>

                                        <div class="mb-4">
                                            <label class="col-form-label">Active Status</label>
                                            <select v-model="violationParameter.is_active" class="form-control"
                                                :disabled="loading">
                                                <option :value="1">Active</option>
                                                <option :value="0">Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div class="text-center mt-3">
                                    <div v-if="loading">
                                        <button type="button" class="btn btn-success mb-3 me-3">
                                            <span
                                                class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                                            Processing
                                        </button>
                                    </div>
                                    <div v-else>
                                        <button type="submit" class="btn btn-success mb-3 me-3" :disabled="loading">
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </template>
                        <div v-else class="alert alert-danger text-center">
                            <strong>Access Denied!</strong> You don't have permission to add violation details.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}
</style>

<script setup>
import { useMeta } from "@/composables/use-meta";
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { BASE_URL } from '@/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

useMeta({ title: "Add Violation Details" });

const router = useRouter();
const store = useStore();

// Form data
const violationParameter = ref({
    violation_id: '',
    name: '',
    parameter_type_id: '',
    is_active: 1
});

// State
const violations = ref([]);
const parameter_types = ref([]);
const loading = ref(false);

// Permission check
const hasCreatePermission = computed(() => {
    return store.getters.hasPermission('create_violation_parameter');
});

// Alert functions
const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

// Data loading functions
const loadParameterTypes = async () => {
    try {
        loading.value = true;
        const response = await axios.get(`${BASE_URL}/parameter-types`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        parameter_types.value = response.data;
    } catch (error) {
        handleApiError(error, 'Failed to load Detail Types');
    } finally {
        loading.value = false;
    }
};

const loadViolations = async () => {
    try {
        loading.value = true;
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data;
    } catch (error) {
        handleApiError(error, 'Failed to load violations');
    } finally {
        loading.value = false;
    }
};

// Form submission
const submitForm = async () => {
    if (!hasCreatePermission.value) {
        showErrorAlert("You don't have permission to add violation details");
        return;
    }

    try {
        loading.value = true;
        await axios.post(
            `${BASE_URL}/violation-parameters`,
            violationParameter.value,
            {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }
        );

        // Reset form and show success message
        violationParameter.value = {
            violation_id: '',
            name: '',
            parameter_type_id: '',
            is_active: 1
        };

        showSuccessAlert('Violation Detail added successfully!');
        router.push('/violation/parameters/view');
    } catch (error) {
        handleApiError(error, 'Failed to add violation detail');
    } finally {
        loading.value = false;
    }
};

// Error handling helper
const handleApiError = (error, defaultMessage) => {
    console.error('API Error:', error);
    let errorMessage = defaultMessage;

    if (error.response) {
        if (error.response.status === 403) {
            errorMessage = "You don't have permission to perform this action";
        } else if (error.response.data?.errors) {
            errorMessage = Object.values(error.response.data.errors).join('\n');
        } else {
            errorMessage = error.response.data?.message || errorMessage;
        }
    } else if (error.request) {
        errorMessage = 'Network error - please check your connection';
    }

    showErrorAlert(errorMessage);
};

// Initialize component
onMounted(async () => {
    if (hasCreatePermission.value) {
        await Promise.all([loadViolations(), loadParameterTypes()]);
    }
});
</script>